import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Moon, Sun, Search, User, LogIn } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useMember } from "@/context/MemberContext";
import { PUBLIC_LAUNCH } from "@/constants/launch";

const COMMUNITY_URL = "https://www.facebook.com/share/g/1LvwmynkeJ/?mibextid=wwXIfr";
const focusRing = "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const links = PUBLIC_LAUNCH ? [
  { to: "/learn", label: "Learn" },
  { to: "/free-apps", label: "Free Apps" },
  { to: "/community", label: "Community & Events" },
  { to: "/resources", label: "Resources" },
  { to: "/certification", label: "Certificates" },
  { to: "/legal", label: "Legal" },
  { to: "/news", label: "News", href: "https://wolfdognews.com/" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
] : [
  { to: "/learn", label: "Learn" },
  { to: "/community", label: "Facebook Community", href: COMMUNITY_URL },
  { to: "/resources", label: "Resources" },
  { to: "/directory", label: "Directory" },
  { to: "/events", label: "Events" },
  { to: "/certification", label: "Certification" },
  { to: "/legal", label: "Legal" },
  { to: "/news", label: "News", href: "https://wolfdognews.com/" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function BrandMark({ className = "" }) {
  return (
    <div className={"flex items-center gap-3 " + className}>
      <svg viewBox="0 0 40 40" className="h-9 w-9" aria-hidden>
        <circle cx="20" cy="20" r="19" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
        <path
          d="M8 26c3-2 4-6 4-8 0 2 1 3 3 3-1 2 0 4 2 5-2 0-3 1-4 3 0-2-2-3-5-3zm24 0c-3-2-4-6-4-8 0 2-1 3-3 3 1 2 0 4-2 5 2 0 3 1 4 3 0-2 2-3 5-3zM20 8c-2 2-3 6-3 8 1-1 2-1 3-1s2 0 3 1c0-2-1-6-3-8z"
          fill="currentColor"
        />
      </svg>
      <div className="leading-none">
        <div className="font-display text-lg tracking-tight">Wolfdog Village</div>
        <div className="eyebrow text-[10px] mt-0.5">Learning Institute</div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { user, logout, error: memberError } = useMember();
  const login = () => { window.location.assign("/login"); };
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-background/85 border-b border-border">
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="text-foreground" data-testid="nav-brand">
          <BrandMark />
        </Link>

        <nav className="hidden 2xl:flex items-center gap-5">
          {links.map(l => l.href ? (
            <a
              key={l.to}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${l.label} (opens in a new tab)`}
              data-testid={`nav-link-${l.to.slice(1)}`}
              className={`text-sm font-medium tracking-wide transition-colors text-muted-foreground hover:text-foreground ${focusRing}`}
            >
              {l.label}
            </a>
          ) : (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                "text-sm font-medium tracking-wide transition-colors " +
                (isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground")
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            aria-label="Toggle theme"
            onClick={toggle}
            data-testid="theme-toggle"
            className="h-10 w-10 grid place-items-center rounded-md hover:bg-muted transition"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {(user ? (
            <div className="flex items-center gap-2"><Link to="/dashboard" data-testid="nav-dashboard" className="btn-primary !py-2 !px-4 text-xs">
              <User className="h-4 w-4" /> {user.name?.split(" ")[0] || "Dashboard"}
            </Link><button onClick={() => logout().catch(() => {})} className="text-xs px-2 py-2">Sign Out</button></div>
          ) : (
            <>
              <button onClick={login} data-testid="nav-login" className="text-sm font-medium hover:text-foreground text-muted-foreground px-3 py-2">
                Member Login
              </button>
              <button onClick={() => { window.location.assign("/register"); }} data-testid="nav-join" className="btn-gold !py-2 !px-4 text-xs">
                Join the Village
              </button>
            </>
          ))}
        </div>

        <button
          className="2xl:hidden h-10 w-10 grid place-items-center"
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(o => !o)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="2xl:hidden border-t border-border bg-background">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map(l => l.href ? (
              <a
                key={l.to}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${l.label} (opens in a new tab)`}
                onClick={() => setOpen(false)}
                className={`text-base font-medium ${focusRing}`}
                data-testid={`nav-m-${l.to.slice(1)}`}
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-base font-medium"
                data-testid={`nav-m-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <button onClick={toggle} className="btn-outline !py-2 !px-4 text-xs">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {theme === "dark" ? "Light" : "Dark"} mode
              </button>
              {(user ? (
                <button onClick={() => logout().catch(() => {})} className="btn-primary !py-2 !px-4 text-xs">Logout</button>
              ) : (
                <button onClick={login} className="btn-gold !py-2 !px-4 text-xs">
                  <LogIn className="h-4 w-4" /> Login
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {memberError && <p role="alert" className="container-x py-3 text-sm">{memberError}</p>}
    </header>
  );
}
